import { useState, Fragment } from 'react'
import { useApplication } from '../../../context/application'
import { PolicyTypeOptions } from 'src/dtos/applicationData'
import { QuoteDataWithId } from 'src/dtos/custom'
import {
  ApplicationBodyContainer, 
  ApplicationBodyContent,
  DefaultTitle, 
  DefaultSubtitle,
  FormContainer,
  PolicyTable,
  PolicyTableContainer,
  PolicyTypeContainer
} from '../../../components/application/defaultComponents'
import { FlexDiv } from 'src/components/application/formComponents/styles'
import ApplicationPrebuiltFooter from '../../../components/application/PrebuiltBodyFooter'
import QuoteDetailBox from 'src/components/application/formComponents/QuoteDetailBox'
import Button from 'src/components/basic/Button'
import produce from 'immer'
import { generateRandomNumberId } from 'src/utils/math'
import { AttorneyQuoteSelectSchema } from 'src/yup/NewAppSchema'

const Select = () => {
  const quoteBoxErrorCodes = [ 'EC296', 'EC297']
  const { application } = useApplication()
  const [showQuoteSelection, setShowQuoteSelection] = useState(false)
  const [quotes, setQuotes] = useState<QuoteDataWithId[]>(() => {
    return application?.quotesSection?.quotes && application?.quotesSection?.quotes.length > 0 ?
      application.quotesSection.quotes.map(quote => ({ ...quote, id: generateRandomNumberId() })) :
      []
  })
  const [showErrors, setShowErrors] = useState(false)

  const handleSetQuote = (index: number, quote: QuoteDataWithId) => {
    setQuotes(produce(draft => {
      draft[index] = quote
    }))
  }

  const handleSelectQuote = (policyType: PolicyTypeOptions) => {
    setQuotes(prev => [...prev, { policyType, id: generateRandomNumberId() }])
    setShowQuoteSelection(false)
  }

  const handleRemoveQuote = (index: number) => {
    setQuotes(prev => prev.filter((_, idx) => idx !== index))
  }



  const validatePage = () => {
    let validations = quotes.map(att => {
      try {
        AttorneyQuoteSelectSchema.validateSync(att, { abortEarly: false })
        return true
      } catch(err) {
        return false
      }
    })

    for (let index = 0; index < quotes.length; index++) {
      const testQuotes = quotes.filter((qt, idx) => idx !== index);

      const duplicate = testQuotes.find(
        qt => 
          qt.deductible === quotes[index].deductible &&
          qt.liabilityLimits === quotes[index].liabilityLimits &&
          qt.policyType === quotes[index].policyType
      )

      if(!!duplicate) {
        validations.push(false)
        break;
      }
    }
    
    return validations
  }

  const updatedApplicationData = () => ({ 
    ...application,
    currentPage: window.location.pathname,
    quotesSection: {
      quotes: quotes.map(quote => {
        const { id, ...rest } = quote
        return rest
      })
    }
  })

  const handleScrollIfInvalid = () => {
    const validations = validatePage()
    if(validations.some(vali => vali === false)) {
      setShowErrors(true)
      const firstInvalidIndex = validations.findIndex(inv => inv === false)
      document.getElementById(`qt__${firstInvalidIndex}`)?.scrollIntoView({ behavior: 'smooth' })
    }
  }
  
  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width='94%'>
        <DefaultTitle>Select the limits of liability and deductible for your quote(s).</DefaultTitle>
        <DefaultSubtitle>
          If you’d like to see a quote for an additional policy you can add it below. 
        </DefaultSubtitle>
        <FormContainer maxWidth='100%'>
          {quotes.map((quote, index) => (
            <Fragment key={quote.id}>
              {index > 0 ? (
                <DefaultSubtitle style={{ fontSize: '16px', margin: '64px 0 -16px' }}>
                  SELECT YOUR POLICY’S LIMITS & DEDUCTIBLE
                </DefaultSubtitle>
              ) : null}
              <div id={'qt__'+index}>
              </div>
              <QuoteDetailBox
                quote={quote}
                setQuote={quote => handleSetQuote(index, quote)}
                showErrors={showErrors}
                errorCodes={quoteBoxErrorCodes.map(e => `${e}:${index}`)}
              />
              {index > 0 ? (
                <FlexDiv maxWidth='320px' height='fit-content'>
                  <Button
                    variant='error'
                    onClick={() => handleRemoveQuote(index)}
                    style={{width: '100%'}}
                  >
                    Remove this quote
                  </Button>
                </FlexDiv>
              ) : null}
            </Fragment>
          ))}
        </FormContainer>
        {showQuoteSelection ? (
          <FlexDiv height='fit-content' maxWidth='600px' margin='64px 0 0 0'>
            <PolicyTableContainer>
              <h6>SELECT POLICY TYPE</h6>
              <PolicyTable>
                <tbody>
                  <tr>
                    <td>
                      <PolicyTypeContainer>
                        <h5>Essential Policy</h5>
                        <p>This is our standard policy. It has some limitations both on services and limits of liability.</p>
                        <Button onClick={() => handleSelectQuote('BASIC')}>Start Quote</Button>
                      </PolicyTypeContainer>
                    </td>
                    <td>
                      <PolicyTypeContainer>
                        <h5>Premier Policy</h5>
                        <p>This is our top policy offering. It has the most service offerings; including these things.</p>
                        <Button onClick={() => handleSelectQuote('REGULAR')}>Start Quote</Button>
                      </PolicyTypeContainer>
                    </td>
                  </tr>
                </tbody>
              </PolicyTable>
            </PolicyTableContainer>
          </FlexDiv>
        ) : null}
        <FlexDiv margin='64px 0 0 0' maxWidth='320px' height='fit-content'>
          <Button
            variant="secondary"
            onClick={() => setShowQuoteSelection(true)}
            disabled={showQuoteSelection}
            style={{width: '100%'}}
          >
            Add another quote
          </Button>
        </FlexDiv>
      </ApplicationBodyContent>
      <ApplicationPrebuiltFooter
        goBackRoute="/quote/type"
        isFormValid={validatePage().every(att => att === true)}
        nextRoute="/review/firm"
        updatedApplication={updatedApplicationData()}
        beforeSaveContinueClick={handleScrollIfInvalid}
      />
    </ApplicationBodyContainer>
  )
}

export default Select